import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { GeneratedSlot } from '../utils/generatedAvailability';
import { formatDateToDateTimeLocal } from '../../BookingConfiguration/utils';

interface SlotCacheContextValue {
  availableSlots: GeneratedSlot[];
  isLoadingSlots: boolean;
  fetchAvailableSlots: (start: Date, end: Date) => Promise<void>;
  getSlotsForDate: (date: string) => GeneratedSlot[];
  isDateInCache: (date: string) => boolean;
}

const SlotCacheContext = createContext<SlotCacheContextValue | null>(null);

interface SlotCacheProviderProps {
  availableSlots: GeneratedSlot[];
  isLoadingSlots: boolean;
  fetchAvailableSlots: (start: Date, end: Date) => Promise<void>;
  children: ReactNode;
}

export const SlotCacheProvider: React.FC<SlotCacheProviderProps> = ({
  availableSlots,
  isLoadingSlots,
  fetchAvailableSlots,
  children,
}) => {
  const getSlotsForDate = useCallback((date: string): GeneratedSlot[] => {
    return availableSlots
      .filter((slot) => {
        const startDate = formatDateToDateTimeLocal(slot.start).split('T')[0];
        const endDate = formatDateToDateTimeLocal(slot.end).split('T')[0];
        return startDate === date || endDate === date;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [availableSlots]);

  const isDateInCache = useCallback((date: string): boolean => {
    if (availableSlots.length === 0) return false;
    const startDates = availableSlots.map(s => formatDateToDateTimeLocal(s.start).split('T')[0]);
    const endDates = availableSlots.map(s => formatDateToDateTimeLocal(s.end).split('T')[0]);
    const allDates = [...new Set([...startDates, ...endDates])];
    return allDates.includes(date);
  }, [availableSlots]);

  const value: SlotCacheContextValue = {
    availableSlots,
    isLoadingSlots,
    fetchAvailableSlots,
    getSlotsForDate,
    isDateInCache,
  };

  return (
    <SlotCacheContext.Provider value={value}>
      {children}
    </SlotCacheContext.Provider>
  );
};

export const useSlotCache = (): SlotCacheContextValue => {
  const context = useContext(SlotCacheContext);
  if (!context) {
    throw new Error('useSlotCache must be used within a SlotCacheProvider');
  }
  return context;
};
